import type { UseQueryOptions } from '@tanstack/react-query';

import { settingsListAlarmSetpoints } from '@crossnokaye/typescript-sdk/funcs/settingsListAlarmSetpoints';
import { Alarmsetpoint } from '@crossnokaye/typescript-sdk/models/components';
import { SDKError } from '@crossnokaye/typescript-sdk/models/errors';
import {
  ListAlarmSetpointsRequest,
  ListAlarmSetpointsResponse,
} from '@crossnokaye/typescript-sdk/models/operations';

import { AtlasClientFunction } from '@/client/types';
import { AtlasQueryWithOrgIdAndAgentId } from '@/queries/types';

// TODO: This is a temporary filter to remove invalid alarm setpoints
const alarmSetpointFilter = (setpoint: Alarmsetpoint) => {
  const exclusions = ['clear delay', 'trip delay', 'deadband', 'alarms enabled', 'alarm enabled'];
  const alarmName = setpoint.alarmDisplayName?.toLowerCase() ?? '';
  const settingName = setpoint.displayName?.toLowerCase() ?? '';
  return !alarmName.includes('invalid') && !exclusions.some((term) => settingName.includes(term));
};

const transformListAlarmSetpointsResponse = (
  response: ListAlarmSetpointsResponse
): Array<Alarmsetpoint> | undefined => {
  const alarmSetpoints = response.listAlarmSetpointsResponseBody;
  if (!alarmSetpoints) {
    return undefined;
  }

  return alarmSetpoints.values?.filter(alarmSetpointFilter) || undefined;
};

export interface ListAlarmSetpointsQueryProps extends AtlasQueryWithOrgIdAndAgentId {
  transform?: (response: ListAlarmSetpointsResponse) => Array<Alarmsetpoint> | undefined;
  client: AtlasClientFunction;
  active: boolean;
}

export const listAlarmSetpointsQuery = ({
  orgId,
  agentId,
  transform = transformListAlarmSetpointsResponse,
  client,
  active,
}: ListAlarmSetpointsQueryProps): UseQueryOptions<
  ListAlarmSetpointsResponse,
  SDKError,
  Array<Alarmsetpoint> | undefined
> => {
  return {
    enabled: !!orgId && !!agentId && active,
    queryKey: [orgId, agentId, 'listAlarmSetpoints'],
    queryFn: () =>
      client<ListAlarmSetpointsRequest, ListAlarmSetpointsResponse>({
        atlasFunc: settingsListAlarmSetpoints,
        request: { orgId: orgId!, agentId: agentId! },
      }),
    select: transform,
    refetchInterval: 5000, // 5 seconds polling to match Darkwing
    refetchIntervalInBackground: true, // Keep polling in background
    staleTime: 5000, // Keep prefetched data fresh for 5 seconds
    refetchOnWindowFocus: false, // Avoid double fetches
    retry: 3, // Retry failed requests
  };
};
